import { useCallback, useState } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'

import LayoutContainer from 'components/Common/LayoutContainer'

import ResponsiveImage from 'components/Common/ResponsiveImage'
import ShareModal from 'components/Common/Modals/ShareModal'
import ShareWithFriendsButton from 'components/Common/ShareWithFriendsButton'
import LineAngleLeftIcon from 'LuxKit/Icons/line/LineAngleLeftIcon'
import SignUpBox from '../SignUpBox'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import type HeroProps from './HeroProps'

import { mediaQueryUp, mediaQueryDown } from 'components/utils/breakpoint'
import { connect } from 'react-redux'
import TextButton from 'LuxKit/Button/TextButton'
import Modal from 'LuxKit/Modal/Modal'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'

const Wrapper = styled.div`
  background-color: ${props => props.theme.palette.neutral.default.eight};
`

const HeadingWrapper = styled.div`
  text-align: center;
  padding-top: ${rem(16)};
  padding-bottom: ${rem(24)};
`

const TextDescription = styled(BodyText)`
  margin: 0 auto;
  margin-top: ${rem(16)};

  ${mediaQueryUp.largeDesktop} {
    max-width: ${rem(700)};
  }
`

const ControlWrapper = styled.div`
  ${mediaQueryDown.tablet} {
    background-color: ${props => props.theme.palette.neutral.default.one};
    color: ${props => props.theme.palette.neutral.default.eight};
  }
`

const ImageWrapper = styled.div`
  margin-bottom: ${rem(40)};

  ${mediaQueryDown.desktop} {
    margin: 0;
    padding: 0 !important;
  }
`

interface Props extends HeroProps {
  shareConfig: any
  headlessMode: boolean
  isStoreMode: boolean
}

function HubPageHero(props: Props) {
  const headlessMode = props.headlessMode
  const [signUpModalOpen, setSignUpModalOpen] = useState(!props.memberId)
  const signUpModal = useCallback(() => {
    setSignUpModalOpen(false)
  }, [])

  return <Wrapper>
    <LayoutContainer>
      <VerticalSpacer gap={12}>
        {!headlessMode &&
          <ControlWrapper>
            {props.back && (
              <TextButton kind="tertiary" variant="ghost" startIcon={<LineAngleLeftIcon />} to={props.back.link}>
                {props.back.title}
              </TextButton>
            )}
            <ShareModal
              shareConfig={props.shareConfig}
            >
              <ShareWithFriendsButton variant="ghost" />
            </ShareModal>
          </ControlWrapper>}
        <HeadingWrapper>
          <Heading
            variant="heading2"
            as="h1"
            data-testid="landing-header"
          >
            {props.title}
          </Heading>
          {props.subTitle?.split('\\n').map(st => (
            <TextDescription
              variant="large"
              forwardedAs="h2"
              key={st}
            >
              {st}
            </TextDescription>
          ))}
        </HeadingWrapper>
        <ImageWrapper>
          <ResponsiveImage
            alt={props.headTitle}
            aria-hidden="true"
            gravity="auto"
            mobileAspectRatio="16:10"
            desktopAspectRatio="21:10"
            largeDesktopAspectRatio="21:9"
            id={props.image}
          />
        </ImageWrapper>
      </VerticalSpacer>
    </LayoutContainer>
    {(!props.memberId || !props.isStoreMode) && (
      <Modal
        isOpen={signUpModalOpen && !props.showAccountModal}
        onClose={signUpModal}
        title=""
      >
        <SignUpBox title={props.title} version="hub" />
      </Modal>
    )}
  </Wrapper>
}

function mapStateToProps(state: App.State) {
  return {
    headlessMode: state.system.headlessMode,
    isStoreMode: state.system.storeMode,
  }
}

export default connect(mapStateToProps)(HubPageHero)
